import { useContext, useState } from 'react';
import { WorkflowContext } from '../contexts';
import { PlusIcon } from '@heroicons/react/outline';
import { useModal } from '../contexts';
import { Transition } from '@headlessui/react';
import WorkflowOverviewBlockSignatureRequest from './WorkflowOverviewBlockSignatureRequest';
import WorkflowOverviewBlockEmail from './WorkflowOverviewBlockEmail';
import WorkflowOverviewBlockReview from './WorkflowOverviewBlockReview';
import WorkflowOverviewBlockDocument from './WorkflowOverviewBlockDocument';
import WorkflowOverviewBlockSource from './WorkflowOverviewBlockSource';
import WorkflowOverviewBlockSheetsWriteToSource from './WorkflowOverviewBlockSheetsWriteToSource';
import WorkflowOverviewBlockGlideWriteToSource from './WorkflowOverviewBlockGlideWriteToSource';
import WorkflowOverviewBlockOutgoingWebhook from './WorkflowOverviewBlockOutgoingWebhook';
import useUser from '../hooks/useUser';
import WorkflowOverviewBlockPlaceholder from './WorkflowOverviewBlockPlaceholder';
import WorkflowSelectModal from './WorkflowSelectModal';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, DragOverlay, DragStartEvent, DragEndEvent, PointerSensor, useSensor, useSensors, closestCenter, UniqueIdentifier } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { patchData } from '../backend';

interface Props {
  show: boolean;
}

function WorkflowOverview(props: Props) {
  const { t } = useTranslation();

  const { openModal } = useModal();
  const { hasPermission } = useUser();

  const workflowContext = useContext(WorkflowContext);
  const { workflow, mutateWorkflow } = workflowContext;

  const canEdit = hasPermission(workflow.team, "workflows.edit");

  // DnD state
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id);
  }

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeIdx = workflow.documents.findIndex(d => d.id === active.id);
    const overIdx = workflow.documents.findIndex(d => d.id === over.id);

    if (activeIdx === -1 || overIdx === -1 || activeIdx === overIdx) return;

    const reorderedDocs = arrayMove(workflow.documents, activeIdx, overIdx);
    const prev = overIdx > 0 ? reorderedDocs[overIdx - 1] : null;
    const next = overIdx < reorderedDocs.length - 1 ? reorderedDocs[overIdx + 1] : null;

    let newFloatOrder: number;
    if (!prev) newFloatOrder = next!.floatOrder / 2;
    else if (!next) newFloatOrder = prev.floatOrder + 16384;
    else newFloatOrder = (prev.floatOrder + next.floatOrder) / 2;

    const movedDoc = reorderedDocs[overIdx];
    const updatedDocs = reorderedDocs.map(d =>
      d.id === movedDoc.id ? { ...d, floatOrder: newFloatOrder } : d
    );

    mutateWorkflow({ ...workflow, documents: updatedDocs }, { revalidate: false });

    try {
      await patchData(`/workflows/documents/${movedDoc.id}/`, { floatOrder: newFloatOrder });
    } catch (error) {
      console.error(error);
    } finally {
      await mutateWorkflow();
    }
  }

  function handleAddBlock(modalProps: any) {
    openModal(<WorkflowSelectModal title={t("Add Workflow Block")} subtitle={t("Add a new block to your workflow")} {...modalProps} workflowContext={workflowContext} />);
  }

  const isComplete = workflow.status === "COMPLETE";

  function FlowConnector(props: { documentPositionFirst?: boolean }) {
    return (
      <div className="group/connector relative flex flex-col items-center">
        <div className={`h-10 flow-connector ${isComplete ? "flow-connector-active" : ""}`} />
        {canEdit && workflow.source &&
          <button
            className={`absolute top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white border-2 border-gray-300 text-gray-300 hover:border-blue hover:text-blue flex items-center justify-center transition-opacity cursor-pointer z-10 ${!isComplete ? "opacity-100" : "opacity-0 group-hover/connector:opacity-100"}`}
            onClick={() => handleAddBlock({ showDocuments: !!workflow.source, showEmails: !!workflow.source, showReview: !!workflow.source && !workflow.review, showSignatureRequest: !!workflow.source && !workflow.signatureConfig && workflow.documents.length >= 1, showRecordOutputs: !!workflow.source, documentPositionFirst: props.documentPositionFirst })}
          >
            <PlusIcon className="w-4 h-4" />
          </button>
        }
      </div>
    );
  }

  let source: JSX.Element | null = null;
  if (workflow.source)
    source = (
      <div className="flex flex-col items-center">
        <WorkflowOverviewBlockSource block={workflow.source} />
      </div>
    )
  else
    source = (
      <div className="flex flex-col items-center">
        <WorkflowOverviewBlockPlaceholder text={t("Add source")} onClick={() => handleAddBlock({ showSources: true })} />
      </div>
    );

  const documentIds = workflow.documents.map(d => d.id);

  let documents: JSX.Element | null = null;
  if (workflow.documents.length > 0)
    documents = (
      <div className='flex flex-col items-center'>
        <FlowConnector documentPositionFirst />
        <SortableContext items={documentIds} strategy={verticalListSortingStrategy}>
          <div className="flex flex-col gap-2">
            {workflow.documents.map((d, index) => (
              <WorkflowOverviewBlockDocument key={d.id} block={d} index={index} />
            ))}
          </div>
        </SortableContext>
      </div>
    )
  else if (workflow.documents.length === 0 && workflow.emails.length === 0)
    documents = (
      <div className="flex flex-col items-center">
        <FlowConnector />
        <WorkflowOverviewBlockPlaceholder text={t("Add template")} onClick={() => handleAddBlock({ showDocuments: true, showEmails: true })} />
      </div>
    );

  let review: JSX.Element | null = null;
  if (workflow.review)
    review = (
      <div className='flex flex-col items-center'>
        <FlowConnector />
        <WorkflowOverviewBlockReview />
      </div>
    );

  let signatureRequestBlocks: JSX.Element | null = null;
  if (workflow.signatureConfig)
    signatureRequestBlocks = (
      <div className='flex flex-col items-center'>
        <FlowConnector />
        <WorkflowOverviewBlockSignatureRequest />
      </div>
    );

  let shareBlocks: JSX.Element | null = null;
  if (workflow.emails.length > 0 || workflow.source?.glideConfig?.isSharingEnabled || workflow.source?.config?.writeToSource || workflow.webhookConfig)
    shareBlocks = (
      <div className='flex flex-col items-center'>
        <FlowConnector />
        {workflow.emails.map((e, index) => (
          <React.Fragment key={e.id}>
            {index > 0 && <div className="h-2" />}
            <WorkflowOverviewBlockEmail block={e} index={index} />
          </React.Fragment>
        ))}
        {workflow.source?.config?.writeToSource &&
          <>
            <FlowConnector />
            <WorkflowOverviewBlockSheetsWriteToSource />
          </>
        }
        {workflow.source?.glideConfig?.isSharingEnabled &&
          <>
            <FlowConnector />
            <WorkflowOverviewBlockGlideWriteToSource />
          </>
        }
        {workflow.webhookConfig &&
          <>
            <FlowConnector />
            <WorkflowOverviewBlockOutgoingWebhook />
          </>
        }
      </div>
    );

  const activeDoc = activeId ? workflow.documents.find(d => d.id === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className={`relative flex ${!props.show ? "hidden" : ""}`} style={{ height: `calc(100vh - 56px)` }}>
        <Transition show={props.show} className="flex flex-col items-center flex-1 p-8 pb-20 overflow-auto h-full">
          {source}
          {documents}
          {/* {workflow.documents.length > 0 &&
            <div className="flex flex-col items-center">
              <FlowConnector />
              <WorkflowOverviewBlockOutputFolder />
            </div>
          } */}
          {review}
          {signatureRequestBlocks}
          {shareBlocks}
          {canEdit && workflow.source && (workflow.documents.length > 0 || workflow.emails.length > 0) &&
            <div className="flex flex-col items-center">
              <div className="h-10 w-[3px] border-l-[3px] border-dashed border-gray-300" />
              <button
                className="relative flex flex-shrink-0 items-center justify-center px-8 py-8 border-dashed border-2 border-gray-300 cursor-pointer rounded w-[600px] h-[114px] text-gray-300 hover:text-blue hover:border-blue transition-all"
                onClick={() => handleAddBlock({ showDocuments: !!workflow.source, showEmails: !!workflow.source, showReview: !!workflow.source && !workflow.review, showSignatureRequest: !!workflow.source && !workflow.signatureConfig && workflow.documents.length >= 1, showRecordOutputs: !!workflow.source })}
              >
                <div className="flex items-center gap-1 text-xl whitespace-nowrap font-gilroy font-semibold">
                  <PlusIcon className="w-6 h-6" />
                  {t("Add Block")}
                </div>
              </button>
            </div>
          }
        </Transition>
      </div>
      <DragOverlay dropAnimation={null}>
        {activeDoc && (
          <div className="pointer-events-none">
            <WorkflowOverviewBlockDocument block={activeDoc} index={0} disableDelete />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

export default WorkflowOverview;
